import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./Modules.scss";
import { errorToast, successToast } from "../../services/ToastHelper";
import { deleteApi, getApi, postApi } from "../../utils/apiService";
import { useEffect, useState } from "react";
import SubHeader from "../../components/SubHeader/SubHeader";
import ResourceCard from "../../components/ResourceCard/ResourceCard";
import {
  ADD_MODULE,
  DELETE_MODULE,
  GET_MODULES_BY_GRADE,
  UPDATE_MODULE,
  ADD_SYLLABUS,
  DELETE_SYLLABUS,
  GET_SYLLABUS,
  UPDATE_SYLLABUS,
  DOWNLOAD_BULK_REPORT,
  GET_ALL_BRANCH,
  GET_ASSESSMENTTYPE_SCHEDULE
} from "../../utils/apiPath";
import Loader from "../../components/Loader/Loader";
import ActionGroup from "../../components/ActionGroup/ActionGroup";
import ActionIcon from "../../components/ActionIcon/ActionIcon";
import { EyeTwoTone, EditTwoTone, DeleteTwoTone, DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import { getPermissionForModule } from "../../services/permissions";
import CustomModal from "../../components/CustomModal/CustomModal";
import InputField from "../../components/InputField/InputField";
import TextAreaField from "../../components/TextAreaField/TextAreaField";
import SelectInput from "../../components/SelectInput/SelectInput";
import { Tabs } from "antd";
import { getStoredBranchOptions } from "../../services/auth";

const SECTIONS = ["A", "B", "C", "D", "E", "F", "G", "H"];

const Modules = () => {
  const { id, gradeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { assessmentTypeName, subject, gradeName } = location.state || {};
  const assessmentLabel = subject ? `${assessmentTypeName} (${subject})` : assessmentTypeName;
  const currentSubTitle = [assessmentLabel, gradeName].filter(Boolean).join(" > ");

  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1"); // "1" Modules, "2" Syllabus, "3" Section Report

  const modulesPermission = getPermissionForModule("Assessment Modules");
  const syllabusPermission = getPermissionForModule("Assessment Syllabus");

  const role = sessionStorage.getItem("role");
  const isAdmin = role === "Admin";
  const sessionBranchId = (() => {
    const raw = sessionStorage.getItem("branchId");
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  })();

  // Branches assigned to the logged-in staff (admins pick from all branches instead)
  const staffBranchOptions = getStoredBranchOptions();
  // Show the branch dropdown for admins, or for staff assigned to more than one branch
  const showBranchSelect = isAdmin || staffBranchOptions.length > 1;

  const [branchOptions, setBranchOptions] = useState([]);
  const [reportBranchId, setReportBranchId] = useState(isAdmin ? 0 : sessionBranchId);
  const [downloadingSection, setDownloadingSection] = useState(null);

  // Schedule filter for the Download Reports tab (null = all schedules)
  const [scheduleOptions, setScheduleOptions] = useState([]);
  const [reportScheduleId, setReportScheduleId] = useState(null);

  // Data states
  const [modulesData, setModulesData] = useState([]);
  const [syllabusData, setSyllabusData] = useState([]);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState("add"); // "add" | "edit"
  const [editingId, setEditingId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [name, setName] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [description, setDescription] = useState("");
  const [marksPerQuestion, setMarksPerQuestion] = useState("");
  const [negativeMarksPerQuestion, setNegativeMarksPerQuestion] = useState("");
  const [nameError, setNameError] = useState("");
  const [sortOrderError, setSortOrderError] = useState("");
  const [marksPerQuestionError, setMarksPerQuestionError] = useState("");
  const [negativeMarksPerQuestionError, setNegativeMarksPerQuestionError] = useState("");

  const fetchAllModules = async () => {
    setIsLoading(true);
    const payload = {
      gradeId: gradeId,
      assessmentTypeId: id,
    };
    const { statusCode, data, message } = await postApi(
      GET_MODULES_BY_GRADE,
      payload,
    );
    if (statusCode === 200) {
      setModulesData(data || []);
    } else {
      errorToast(message);
    }
    setIsLoading(false);
  };

  const fetchAllSyllabus = async () => {
    setIsLoading(true);
    const payload = {
      assessmentTypeId: id,
      gradeId: gradeId,
    };
    const { statusCode, data, message } = await postApi(GET_SYLLABUS, payload);
    if (statusCode === 200) {
      setSyllabusData(data || []);
    } else {
      errorToast(message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (activeTab === "1") {
      fetchAllModules();
    } else if (activeTab === "2") {
      fetchAllSyllabus();
    }
  }, [activeTab]);

  const fetchBranches = async () => {
    const { statusCode, data } = await getApi(GET_ALL_BRANCH);
    if (statusCode === 200) {
      const list = Array.isArray(data) ? data : data?.data || [];
      setBranchOptions(
        list.map((b) => ({ label: b.name, value: b.branchId }))
      );
    }
  };

  useEffect(() => {
    if (activeTab === "3" && isAdmin && branchOptions.length === 0) {
      fetchBranches();
    }
  }, [activeTab, isAdmin]);

  const fetchSchedules = async () => {
    const { statusCode, data } = await getApi(GET_ASSESSMENTTYPE_SCHEDULE, {
      params: { assessmentTypeId: id, attendedOnly: true },
    });
    if (statusCode === 200) {
      const list = Array.isArray(data) ? data : data?.data || [];
      setScheduleOptions(
        list.map((s) => ({
          label: `${s.scheduledDate} ${s.scheduledTime}`,
          value: s.id,
        }))
      );
    }
  };

  useEffect(() => {
    if (activeTab === "3" && scheduleOptions.length === 0) {
      fetchSchedules();
    }
  }, [activeTab]);

  const handleDownloadSectionReport = async (sectionName) => {
    if (isAdmin && !reportBranchId) {
      errorToast("Please select a branch");
      return;
    }
    setDownloadingSection(sectionName);
    try {
      const payload = {
        branchId: reportBranchId || sessionBranchId,
        gradeId: Number(gradeId) || 0,
        assessmentTypeId: Number(id) || 0,
        sectionName,
        assessmentTypeScheduleId: reportScheduleId ?? null,
      };
      const blob = await postApi(DOWNLOAD_BULK_REPORT, payload, {
        responseType: "blob",
      });
      if (!(blob instanceof Blob) || blob.size === 0) {
        errorToast("No report available for this section");
        return;
      }
      if (blob.type && blob.type.includes("application/json")) {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text);
          errorToast(parsed?.message || "No report available for this section");
        } catch {
          errorToast("No report available for this section");
        }
        return;
      }
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `Section-${sectionName}-Grade-${gradeId}-Report.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      successToast("Report downloaded");
    } finally {
      setDownloadingSection(null);
    }
  };

  const handleOpenAdd = () => {
    setMode("add");
    setEditingId(null);
    setName("");
    setSortOrder(activeTab === "1" ? modulesData.length + 1 : syllabusData.length + 1);
    setDescription("");
    setMarksPerQuestion("");
    setNegativeMarksPerQuestion("");
    setNameError("");
    setSortOrderError("");
    setMarksPerQuestionError("");
    setNegativeMarksPerQuestionError("");
    setIsModalOpen(true);
  };

  const handleEdit = (record) => {
    setMode("edit");
    setEditingId(record?.id);
    setName(record?.name || record?.module_name || "");
    setSortOrder(record?.sortOrder ?? record?.SortOrder ?? record?.sort_order ?? "");
    setDescription(record?.description || "");
    setMarksPerQuestion(record?.marksPerQuestion ?? record?.MarksPerQuestion ?? "");
    setNegativeMarksPerQuestion(record?.negativeMarksPerQuestion ?? record?.NegativeMarksPerQuestion ?? "");
    setNameError("");
    setSortOrderError("");
    setMarksPerQuestionError("");
    setNegativeMarksPerQuestionError("");
    setIsModalOpen(true);
  };

  const handleViewModule = (record) => {
    // Modules navigate to Questions
    navigate(
      `/questions/${id}/${gradeId}/${record.id}`, {
      state: {
        assessmentTypeName,
        subject,
        gradeName,
        moduleName: record?.name || record?.module_name
      }
    }
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async () => {
    const nameTrimmed = name.trim();
    let hasError = false;
    if (!nameTrimmed) {
      setNameError(`${activeTab === "1" ? "Module" : "Syllabus"} name is required`);
      hasError = true;
    }
    if (sortOrder === "" || sortOrder === null) {
      setSortOrderError("Sort order is required");
      hasError = true;
    }
    const sortOrderNum = Number(sortOrder);
    if (Number.isNaN(sortOrderNum) || sortOrderNum < 0) {
      setSortOrderError("Sort order must be a valid number");
      hasError = true;
    }

    // Marks fields apply only to Modules
    const isModule = activeTab === "1";
    const marksNum = Number(marksPerQuestion);
    const negativeMarksNum = Number(negativeMarksPerQuestion);
    if (isModule) {
      if (marksPerQuestion === "" || marksPerQuestion === null) {
        setMarksPerQuestionError("Marks per question is required");
        hasError = true;
      } else if (Number.isNaN(marksNum) || marksNum < 0) {
        setMarksPerQuestionError("Marks per question must be a valid number");
        hasError = true;
      }
      if (negativeMarksPerQuestion === "" || negativeMarksPerQuestion === null) {
        setNegativeMarksPerQuestionError("Negative marks per question is required");
        hasError = true;
      } else if (Number.isNaN(negativeMarksNum) || negativeMarksNum < 0) {
        setNegativeMarksPerQuestionError("Negative marks per question must be a valid number");
        hasError = true;
      }
    }
    if (hasError) return;

    setIsSubmitting(true);
    try {
      const payload = {
        name: nameTrimmed,
        sortOrder: sortOrderNum,
        description: description.trim() || "",
        gradeId,
        assessmentTypeId: id,
      };

      if (isModule) {
        payload.marksPerQuestion = marksNum;
        payload.negativeMarksPerQuestion = negativeMarksNum;
      }

      if (activeTab === "2") {
        // If Syllabus, we might need moduleId? 
        // User didn't specify, but usually syllabus belongs to something.
        // For now using the payload structure from Syllabus.jsx
      }

      const API_URL = activeTab === "1"
        ? (mode === "add" ? ADD_MODULE : UPDATE_MODULE)
        : (mode === "add" ? ADD_SYLLABUS : UPDATE_SYLLABUS);

      if (mode === "edit") payload.id = editingId;

      const { statusCode, message } = await postApi(API_URL, payload);

      if (statusCode === 200) {
        successToast(message);
        setIsModalOpen(false);
        if (activeTab === "1") await fetchAllModules();
        else await fetchAllSyllabus();
      } else {
        errorToast(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (record) => {
    setIsLoading(true);
    try {
      const DELETE_URL = activeTab === "1" ? DELETE_MODULE : DELETE_SYLLABUS;
      const { statusCode, message } = await deleteApi(DELETE_URL, {
        params: { id: record?.id },
      });

      if (statusCode === 200) {
        successToast(message);
        if (activeTab === "1") await fetchAllModules();
        else await fetchAllSyllabus();
      } else {
        errorToast(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const moduleItems = (
    <div className="resource-grid">
      {modulesData.map((item) => (
        <ResourceCard
          key={item.id}
          title={item.name}
          subTitle={
            <>
              <span style={{ display: "block" }}>Sort Order: {item.sortOrder ?? item.sort_order ?? '-'}</span>
              <span style={{ display: "block" }}>Marks / Question: {item.marksPerQuestion ?? item.MarksPerQuestion ?? '-'}</span>
              <span style={{ display: "block" }}>Negative Marks / Question: {item.negativeMarksPerQuestion ?? item.NegativeMarksPerQuestion ?? '-'}</span>
            </>
          }
          onClick={modulesPermission?.canView ? () => handleViewModule(item) : undefined}
          actions={
            <ActionGroup>
              {modulesPermission?.canView && (
                <ActionIcon
                  title="View Questions"
                  icon={<EyeTwoTone twoToneColor={["#0e82fd", "#e6f4ff"]} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewModule(item);
                  }}
                />
              )}
              {modulesPermission?.canEdit && (
                <ActionIcon
                  title="Edit"
                  icon={<EditTwoTone twoToneColor={["#52c41a", "#f6ffed"]} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(item);
                  }}
                />
              )}
              {modulesPermission?.canDelete && (
                <ActionIcon
                  title="Delete"
                  icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                  confirm={{
                    enabled: true,
                    title: `Delete "${item.name}"?`,
                    okText: "Delete",
                    cancelText: "Cancel",
                    onConfirm: () => handleDelete(item),
                  }}
                />
              )}
            </ActionGroup>
          }
        />
      ))}
      {modulesData.length === 0 && !isLoading && (
        <div className="no-data">No modules found</div>
      )}
    </div>
  );

  const syllabusItems = (
    <div className="resource-grid">
      {syllabusData.map((item) => (
        <ResourceCard
          key={item.id}
          title={item.name}
          subTitle={`Sort Order: ${item.sortOrder || item.SortOrder || '-'}`}
          actions={
            <ActionGroup>
              {syllabusPermission?.canEdit && (
                <ActionIcon
                  title="Edit"
                  icon={<EditTwoTone twoToneColor={["#52c41a", "#f6ffed"]} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(item);
                  }}
                />
              )}
              {syllabusPermission?.canDelete && (
                <ActionIcon
                  title="Delete"
                  icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                  confirm={{
                    enabled: true,
                    title: `Delete "${item.name}"?`,
                    okText: "Delete",
                    cancelText: "Cancel",
                    onConfirm: () => handleDelete(item),
                  }}
                />
              )}
            </ActionGroup>
          }
        />
      ))}
      {syllabusData.length === 0 && !isLoading && (
        <div className="no-data">No syllabus found</div>
      )}
    </div>
  );

  const sectionReportItems = (
    <div className="section-report">
      <div className="section-report-filters">
        {showBranchSelect && (
          <SelectInput
            title="Branch"
            placeholder="Select branch"
            value={reportBranchId || undefined}
            options={isAdmin ? branchOptions : staffBranchOptions}
            onChange={(val) => setReportBranchId(val || 0)}
          />
        )}
        <SelectInput
          title="Schedule"
          placeholder="All schedules"
          value={reportScheduleId ?? undefined}
          options={scheduleOptions}
          allowClear
          onChange={(val) => setReportScheduleId(val ?? null)}
        />
      </div>

      {isAdmin && !reportBranchId ? (
        <div className="no-data">Please select a branch to view section reports</div>
      ) : (
        <div className="resource-grid">
          {SECTIONS.map((section) => (
            <ResourceCard
              key={section}
              title={`Section ${section}`}
              subTitle={gradeName || `Grade ${gradeId}`}
              actions={
                <ActionGroup>
                  <ActionIcon
                    title={downloadingSection === section ? "Downloading..." : "Download Report"}
                    icon={
                      downloadingSection === section
                        ? <LoadingOutlined style={{ color: "#0e82fd", fontSize: 18 }} spin />
                        : <DownloadOutlined style={{ color: "#0e82fd", fontSize: 18 }} />
                    }
                    onClick={() => handleDownloadSectionReport(section)}
                    disabled={downloadingSection !== null}
                  />
                </ActionGroup>
              }
            />
          ))}
        </div>
      )}
    </div>
  );

  const tabItems = [
    {
      key: "1",
      label: "Modules",
      children: moduleItems,
    },
    {
      key: "2",
      label: "Syllabus",
      children: syllabusItems,
    },
    {
      key: "3",
      label: "Download Reports",
      children: sectionReportItems,
    },
  ];

  return (
    <div className="assessment">
      {isLoading && <Loader />}

      <SubHeader
        title={
          activeTab === "1"
            ? "Modules"
            : activeTab === "2"
              ? "Syllabus"
              : "Section Report"
        }
        subTitle={currentSubTitle}
        showBack={true}
        showRight={(activeTab === "1" && modulesPermission?.canAdd) || (activeTab === "2" && syllabusPermission?.canAdd)}
        rightActionLabel={
          (activeTab === "1" && modulesPermission?.canAdd) ? "Add New Module" :
            (activeTab === "2" && syllabusPermission?.canAdd) ? "Add New Syllabus" : undefined
        }
        onRightClick={
          (activeTab === "1" && modulesPermission?.canAdd) ? handleOpenAdd :
            (activeTab === "2" && syllabusPermission?.canAdd) ? handleOpenAdd : undefined
        }
        onBack={handleBack}
      />

      <div className="modules-tabs-container" style={{ padding: "0 24px" }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="custom-tabs"
        />
      </div>

      <CustomModal
        open={isModalOpen}
        title={mode === "edit" ? `Edit ${activeTab === "1" ? "Module" : "Syllabus"}` : `Add ${activeTab === "1" ? "Module" : "Syllabus"}`}
        onClose={handleCloseModal}
        dangerText="Cancel"
        primaryText={mode === "edit" ? "Update" : "Submit"}
        onPrimary={handleSubmit}
        onDanger={handleCloseModal}
        primaryProps={{ loading: isSubmitting }}
      >
        <InputField
          title={`${activeTab === "1" ? "Module" : "Syllabus"} Name`}
          placeholder={`Enter ${activeTab === "1" ? "module" : "syllabus"} name`}
          value={name}
          required
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
          errorText={nameError}
        />
        <InputField
          title="Sort Order"
          type="number"
          placeholder="Enter sort order"
          value={sortOrder}
          required
          onChange={(e) => {
            setSortOrder(e.target.value);
            if (sortOrderError) setSortOrderError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSubmit();
          }}
          errorText={sortOrderError}
        />
        {activeTab === "1" && (
          <>
            <InputField
              title="Marks Per Question"
              type="number"
              placeholder="Enter marks per question"
              value={marksPerQuestion}
              required
              onChange={(e) => {
                setMarksPerQuestion(e.target.value);
                if (marksPerQuestionError) setMarksPerQuestionError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
              errorText={marksPerQuestionError}
            />
            <InputField
              title="Negative Marks Per Question"
              type="number"
              placeholder="Enter negative marks per question"
              value={negativeMarksPerQuestion}
              required
              onChange={(e) => {
                setNegativeMarksPerQuestion(e.target.value);
                if (negativeMarksPerQuestionError) setNegativeMarksPerQuestionError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmit();
              }}
              errorText={negativeMarksPerQuestionError}
            />
          </>
        )}
        <TextAreaField
          title="Description"
          placeholder={`Enter ${activeTab === "1" ? "module" : "syllabus"} description`}
          value={description}
          rows={3}
          onChange={(e) => setDescription(e.target.value)}
        />
      </CustomModal>
    </div>
  );
};

export default Modules;
